<template>
  <div class="w-full">
    <!-- Desktop Stepper -->
    <div v-if="currentLayout === 'desktop'">
      <Stepper
        v-model:value="activeStep"
        class="flex items-start justify-start"
        vertical
      >
        <!-- Step List -->
        <StepList
          class="gap-6 flex-col items-start min-w-[300px] w-full max-w-[300px]"
          role="list"
        >
          <Step
            v-for="(step, index) in steps"
            :key="index"
            v-slot="{ activateCallback, value }"
            asChild
            :value="index + 1"
            role="presentation"
          >
            <div
              class="flex flex-col flex-auto w-full relative p-2"
              role="presentation"
            >
              <div class="flex items-center gap-2 w-full z-10 relative">
                <button
                  class="bg-transparent inline-flex flex-col gap-2 focus:outline-none focus:ring rounded-full"
                  @click="activateCallback"
                  role="button"
                  :id="`step-${index}`"
                  :tabindex="activeStep === index + 1 ? 0 : -1"
                  :aria-current="value === activeStep ? 'step' : undefined"
                  @keydown="(e) => handleKeydown(e, index)"
                  type="button"
                  :aria-label="`Step ${index + 1}: ${step.title}`"
                >
                  <!-- SVG Circle Check -->
                  <span
                    :class="[
                      'rounded-full size-7 inline-flex items-center justify-center border-2 transition-all duration-300',
                      {
                        'bg-[#16a286] border-[#16a286]': value <= activeStep,
                        'border-[#16a286] bg-white': value == activeStep,
                        'bg-gray-300 border-gray-300': value > activeStep,
                      },
                    ]"
                  >
                    <!-- White checkmark -->
                    <svg
                      v-if="value < activeStep"
                      xmlns="http://www.w3.org/2000/svg"
                      class="w-4 h-4 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      stroke-width="3"
                      aria-label="step"
                      role="img"
                      aria-hidden="true"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>

                    <!-- Green checkmark -->
                    <svg
                      v-else-if="value == activeStep"
                      xmlns="http://www.w3.org/2000/svg"
                      class="w-4 h-4 text-[#16a286]"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      stroke-width="3"
                      aria-label="step"
                      role="img"
                      aria-hidden="true"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>

                    <!-- Gray checkmark -->
                    <svg
                      v-else
                      xmlns="http://www.w3.org/2000/svg"
                      class="w-4 h-4 text-gray-500"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      stroke-width="3"
                      aria-hidden="true"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </span>
                </button>

                <!-- Step Title -->
                <p
                  @click="activateCallback"
                  class="cursor-pointer text-sm"
                  :class="{
                    'line-through font-medium ': value < activeStep,
                    ' font-medium': value > activeStep,
                  }"
                >
                  {{ step.heading }}
                </p>
              </div>

              <!-- Vertical Divider -->
              <div
                v-if="index !== steps.length - 1"
                class="absolute left-[20px] top-8 w-[2px] h-full bg-gray-300"
                :class="{ '!bg-[#16a286]': value < activeStep }"
              ></div>
            </div>
          </Step>
        </StepList>

        <!-- Step Panels -->
        <StepPanels>
          <StepPanel
            v-for="(step, index) in steps"
            :key="index"
            :value="index + 1"
            class="flex flex-col items-center text-center !bg-transparent relative"
            role="tabpanel"
            :id="`step-panel-${index}`"
            :aria-labelledby="`step-${index}`"
            :hidden="activeStep !== index + 1"
            tabindex="0"
            :aria-live="activeStep === index + 1 ? 'polite' : undefined"
            aria-controls=""
          >
            <NuxtImg
              :src="step.image"
              :alt="step.heading ?? 'StepperImg'"
              width="560"
              height="372"
              loading="lazy"
              format="webp"
            />
            <h2 class="text-[var(--primary)] text-xl font-semibold mb-4 mt-10">
              {{ step.heading }}
            </h2>
            <div v-html="step.text"></div>

            <h2
              v-if="steps[activeStep - 1]?.tagHeading"
              class="font-semibold text-2xl my-6"
            >
              {{ step.tagHeading }}
            </h2>

            <div v-if="step.tag" class="flex flex-wrap justify-center gap-2">
              <WrTag v-for="tag in step?.tag" :key="tag">{{ tag }}</WrTag>
            </div>

            <!-- Prev/Next Buttons -->
            <div class="absolute inset-0 flex pointer-events-none">
              <button
                class="w-full h-full pointer-events-auto cursor-left-arrow"
                @click="prevStep"
                :disabled="activeStep === 1"
                type="button"
                aria-label="Previous Step"
              >
                <span
                  class="absolute left-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity"
                  aria-hidden="true"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="w-8 h-8 text-gray-700"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                </span>
              </button>

              <button
                class="w-full h-full pointer-events-auto cursor-right-arrow"
                @click="nextStep"
                :disabled="activeStep === steps.length"
                type="button"
                aria-label="Next Step"
              >
                <span
                  class="absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity"
                  aria-hidden="true"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="w-8 h-8 text-gray-700"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </span>
              </button>
            </div>
          </StepPanel>
        </StepPanels>
      </Stepper>
    </div>

    <!-- Mobile Version -->
    <div
      v-else
      class="flex flex-col items-start justify-center text-center relative"
    >
      <!-- Navigation Row -->
      <div class="flex justify-between w-full gap-8">
        <button
          @click="prevStep"
          :disabled="activeStep === 1"
          class="disabled:opacity-40 flex items-start"
        >
          <NuxtImg
            src="/images/arrow-previous.webp"
            alt="Previous"
            width="50"
            height="34"
            loading="lazy"
            class=""
            format="webp"
          />
        </button>

        <!-- Center Progress -->
        <div class="flex flex-col items-center grow">
          <div class="flex items-center w-full">
            <div
              class="h-[2px] flex-1"
              :class="activeStep > 1 ? 'bg-[#16a286]' : 'bg-gray-300'"
            ></div>
            <div
              class="rounded-full border-2 size-8 flex items-center justify-center mx-2"
              :class="
                activeStep <= steps.length
                  ? 'border-[#16a286] bg-white'
                  : 'border-gray-300 bg-gray-100'
              "
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="w-4 h-4 text-[#16a286]"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="3"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <div
              class="h-[2px] flex-1"
              :class="
                activeStep < steps.length ? 'bg-gray-300' : 'bg-[#16a286]'
              "
            ></div>
          </div>
          <p class="mt-1 font-medium text-sm">
            {{ steps[activeStep - 1]?.heading }}
          </p>
        </div>

        <button
          @click="nextStep"
          :disabled="activeStep === steps.length"
          class="disabled:opacity-40 flex items-start"
        >
          <NuxtImg
            src="/images/arrow-next.webp"
            alt="Next"
            width="50"
            loading="lazy"
            height="34"
            format="webp"
          />
        </button>
      </div>

      <!-- Mobile Step Content -->
      <div class="mt-4 flex flex-col items-center mx-auto">
        <NuxtImg
          :src="steps[activeStep - 1]?.image"
          :alt="steps[activeStep - 1]?.heading ?? 'StepperImg'"
          width="529"
          loading="lazy"
          height="352"
          format="webp"
        />

        <h2 class="text-[var(--primary)] text-xl font-semibold mt-12">
          {{ steps[activeStep - 1]?.heading }}
        </h2>
        <p
          class="mt-2 text-sm [&>li]:my-4"
          v-html="steps[activeStep - 1]?.text"
        ></p>

        <h2
          v-if="steps[activeStep - 1]?.tagHeading"
          class="text-lg font-semibold mb-4 mt-8"
        >
          {{ steps[activeStep - 1]?.tagHeading }}
        </h2>

        <div
          v-if="steps[activeStep - 1].tag"
          class="flex flex-wrap justify-center gap-2"
        >
          <WrTag v-for="tag in steps[activeStep - 1]?.tag">{{ tag }}</WrTag>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from "vue";

const { isMobile, isDesktop } = useDevice();

const props = defineProps({
  steps: { type: Array, required: true },
  initialStep: { type: Number, default: 1 },
  layout: {
    type: String,
    default: "desktop",
    validator: (value) => ["desktop", "mobile"].includes(value),
  },
});

const currentLayout = computed(() => {
  if (props.layout) {
    return props.layout; // forcefully use prop if provided
  }
  return isMobile ? "mobile" : "desktop"; // fallback to auto-detection
});

const activeStep = ref(props.initialStep);

const prevStep = () => {
  if (activeStep.value > 1) activeStep.value--;
};

const nextStep = () => {
  if (activeStep.value < props.steps.length) activeStep.value++;
};

const handleKeydown = (event, index) => {
  if (event.key === "ArrowRight" || event.key === "ArrowDown") {
    nextStep();
    event.preventDefault();
  } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
    prevStep();
    event.preventDefault();
  }
};
</script>

<style scoped>
.cursor-left-arrow {
  cursor: url("/images/arrow-previous.webp"), auto;
}

.cursor-right-arrow {
  cursor: url("/images/arrow-next.webp"), auto;
}
</style>
